import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgbModal, NgbModalRef, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Apollo } from 'apollo-angular';
import { ToastrService } from 'ngx-toastr';
import { MomentModule } from 'ngx-moment';
import { firstValueFrom, Subscription } from 'rxjs';
import { HeaderComponent } from '../../header/header.component';
import { NavComponent } from '../../navigation/nav.component';
import { FleetRegistryBatchComponent } from '../fleet-registry-batch/fleet-registry-batch.component';
import { ADMIN_FLEET_DETAIL, ADMIN_FLEET_VEHICLE_REGISTRIES } from '@gql_queries/taxi_fleet/queries';
import {
  ACTIVATE_FLEET_OPERATOR,
  ADMIN_UPDATE_FLEET_VEHICLE_REGISTRY_STATUS,
  APPROVE_FLEET_ENROLLMENT,
  DEACTIVATE_FLEET_OPERATOR,
  DECLINE_FLEET_ENROLLMENT,
  RESEND_FLEET_LOGIN_CREDENTIALS,
  REVIEW_FLEET_DOCUMENT,
  TAG_FLEET_ENROLLMENT_FOR_AMENDMENT,
  UPDATE_FLEET_OPERATOR_DETAILS,
  UPDATE_TAXI_FLEET_ACCOUNT_STATUS,
} from '@gql_queries/taxi_fleet/mutations';
import { FleetDocType, FLEET_DOC_TYPE_LABELS } from '@enums/fleet_doc_type_enums';

type TaxiFleetStatus = 'APPROVED' | 'DECLINE' | 'FOR_APPROVAL';
type DetailsTab = 'details' | 'registry';
type RegistryStatus = 'pending' | 'active' | 'inactive' | 'released';
type FleetDocumentReviewStatus = 'FOR_AMENDMENT' | 'APPROVED';

interface TaxiFleetAccountHolder {
  fleetAccountId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  role: string;
  isActive: boolean;
  isAccountHolder: boolean;
  isEmailVerified?: boolean | null;
  isMobileVerified?: boolean | null;
  lastLogin?: string | null;
  dateCreated: string;
  dateUpdated: string;
}

interface TaxiFleetDocument {
  fleetDocumentId: string;
  documentUrl: string;
  documentType?: FleetDocType | null;
  status?: string | null;
  remarks?: string | null;
  dateUpdated?: string | null;
}

interface FleetDocumentApprovalHistory {
  fleetAuditTrailId: string;
  fleetDocumentId?: string | null;
  documentType?: FleetDocType | null;
  documentUrl?: string | null;
  action: string;
  status?: string | null;
  remarks?: string | null;
  actorType: string;
  adminId?: string | null;
  adminName?: string | null;
  fleetAccountId?: string | null;
  dateCreated: string;
}

interface TaxiFleetDetail {
  fleetId: string;
  fleetCode: string;
  operatorName: string;
  registeredBusinessName: string;
  status: string;
  declineRemarks: string | null;
  dateApproved: string | null;
  dateCreated: string;
  dateUpdated: string;
  representativeFirstName?: string | null;
  representativeLastName?: string | null;
  representativeMobile?: string | null;
  representativeEmail?: string | null;
  ltfrbCpcNumber?: string | null;
  fleetType?: string | null;
  cpcExpirationDate?: string | null;
  allowedActiveVehicles?: number | null;
  documents?: TaxiFleetDocument[] | null;
  missingRequirements?: string[] | null;
  documentApprovalHistory?: FleetDocumentApprovalHistory[] | null;
  accountHolder: TaxiFleetAccountHolder | null;
}

interface FleetRegistryAssociatedPartner {
  partnerId: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  membershipId: string;
  mobile: string;
  email?: string | null;
  isOnline?: boolean | null;
}

interface FleetRegistryItem {
  fleetVehicleRegistryId: string;
  fleetId: string;
  fleetAccountId?: string;
  plateNumber: string;
  conductionNumber: string;
  vehicleType: string;
  vehicleModel: string;
  yearModel: number;
  vehicleMaker?: string | null;
  vehicleId?: string | null;
  status: RegistryStatus | string;
  dateCreated?: string;
  dateUpdated?: string;
  hasAssociatedPartner: boolean;
  associatedPartner?: FleetRegistryAssociatedPartner | null;
}

interface FleetEditForm {
  operatorName: string;
  registeredBusinessName: string;
  representativeFirstName: string;
  representativeLastName: string;
  representativeMobile: string;
  representativeEmail: string;
  ltfrbCpcNumber: string;
  fleetType: string;
  cpcExpirationDate: string;
  allowedActiveVehicles: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminMobile: string;
}

const PH_MOBILE_REGEX = /^(09\d{9}|639\d{9})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-taxi-fleet-details',
  standalone: true,
  imports: [HeaderComponent, NavComponent, CommonModule, FormsModule, RouterModule, FleetRegistryBatchComponent, MomentModule],
  templateUrl: './taxi-fleet-details.component.html',
  styleUrl: './taxi-fleet-details.component.css',
})
export class TaxiFleetDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('updateStatusModal') updateStatusModal?: TemplateRef<any>;

  fleet: TaxiFleetDetail | null = null;
  isLoading = false;
  isUpdatingStatus = false;
  isReviewingDocument = false;

  /** Document type keys used when these documents are uploaded (see taxi-fleet-create.component.ts uploadDocument()). */
  readonly documentTypeIndex = {
    validId: FleetDocType.VALID_ID,
    cpcDocument: FleetDocType.CPC_DOCUMENT,
  } as const;

  getDocumentLabel(doc: TaxiFleetDocument, index: number): string {
    return (doc.documentType && FLEET_DOC_TYPE_LABELS[doc.documentType])
      || `Document ${index + 1}`;
  }

  get validIdDocument(): TaxiFleetDocument | null {
    return this.fleet?.documents?.find((doc) => doc.documentType === this.documentTypeIndex.validId) || null;
  }

  get cpcDocument(): TaxiFleetDocument | null {
    return this.fleet?.documents?.find((doc) => doc.documentType === this.documentTypeIndex.cpcDocument) || null;
  }

  get otherDocuments(): TaxiFleetDocument[] {
    return (this.fleet?.documents || []).filter(
      (doc) => doc.documentType !== this.documentTypeIndex.validId && doc.documentType !== this.documentTypeIndex.cpcDocument
    );
  }
  detailError = '';
  statusUpdateError = '';
  statusFormError = '';
  failedDocumentIds = new Set<string>();

  activeTab: DetailsTab = 'details';
  fleetId = '';

  registryItems: FleetRegistryItem[] = [];
  isRegistryLoading = false;
  registryError = '';
  isUpdatingRegistryStatus = false;
  selectedRegistry: FleetRegistryItem | null = null;

  statusOptions: { value: TaxiFleetStatus; label: string }[] = [
    { value: 'APPROVED', label: 'Approved' },
    { value: 'DECLINE', label: 'Decline' },
    { value: 'FOR_APPROVAL', label: 'For Approval' },
  ];

  selectedStatus: TaxiFleetStatus = 'APPROVED';
  declineRemarks = '';

  // Non-document fields (CPC number, expiration) still batch via tagFleetEnrollmentForAmendment.
  stagedAmendments: { fieldName: string; message: string }[] = [];
  pendingFieldName = '';
  pendingMessage = '';

  pendingReviewDoc: TaxiFleetDocument | null = null;
  pendingReviewStatus: FleetDocumentReviewStatus | null = null;
  documentReviewRemarks = '';

  readonly fleetTypeOptions = [
    { value: 'taxi', label: 'Taxi' },
    { value: 'tnvs', label: 'TNVS' },
    { value: 'truck', label: 'Truck' },
  ];

  editForm: FleetEditForm = {
    operatorName: '',
    registeredBusinessName: '',
    representativeFirstName: '',
    representativeLastName: '',
    representativeMobile: '',
    representativeEmail: '',
    ltfrbCpcNumber: '',
    fleetType: 'taxi',
    cpcExpirationDate: '',
    allowedActiveVehicles: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminMobile: '',
  };
  editFieldErrors: Partial<Record<keyof FleetEditForm, string>> = {};
  editFormError = '';
  isSavingEdit = false;
  isResendingCredentials = false;

  private queryParamSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apollo: Apollo,
    private modalService: NgbModal,
    private offcanvasService: NgbOffcanvas,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const fleetId = this.route.snapshot.paramMap.get('id');
    if (!fleetId) {
      this.router.navigate(['/admin/taxi-fleet']);
      return;
    }

    this.fleetId = fleetId;
    this.fetchFleetDetail(fleetId);

    this.queryParamSub = this.route.queryParamMap.subscribe((params) => {
      const tab = params.get('tab');
      this.activeTab = tab === 'registry' ? 'registry' : 'details';
      if (this.activeTab === 'registry') {
        void this.fetchFleetRegistry();
      }
    });
  }

  ngOnDestroy(): void {
    this.queryParamSub?.unsubscribe();
  }

  setActiveTab(tab: DetailsTab): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: tab === 'registry' ? { tab: 'registry' } : {},
      replaceUrl: true,
    });
  }

  fetchFleetDetail(fleetId: string): void {
    this.isLoading = true;
    this.detailError = '';

    this.apollo.query({
      fetchPolicy: 'network-only',
      query: ADMIN_FLEET_DETAIL,
      variables: {
        fleetId,
      },
    }).subscribe({
      next: ({ data }: any) => {
        this.fleet = data?.adminFleetDetail || null;
        this.failedDocumentIds.clear();
        this.isLoading = false;

        if (!this.fleet) {
          this.detailError = 'Fleet Operator record not found. Please check and try again.';
        }
      },
      error: (error) => {
        this.detailError = error?.message || 'Connection not available. Please try again later.';
        this.fleet = null;
        this.isLoading = false;
      },
    });
  }

  getStagedAmendment(fieldName: string): { fieldName: string; message: string } | undefined {
    return this.stagedAmendments.find((item) => item.fieldName === fieldName);
  }

  get canReviewDocuments(): boolean {
    const status = (this.fleet?.status || '').toUpperCase();
    // FOR_AMENDMENT: allow tagging additional docs while enrollment is already in amendment
    return status === 'FOR_APPROVAL' || status === 'APPROVED' || status === 'FOR_AMENDMENT';
  }

  get reviewableDocuments(): TaxiFleetDocument[] {
    return (this.fleet?.documents || []).filter((doc) => !!doc.fleetDocumentId);
  }

  /** OP2-209 / OP2-214: enrollment approve blocked while docs need amendment, staged fields, or missing requirements */
  get hasDocumentsForAmendment(): boolean {
    return this.reviewableDocuments.some(
      (doc) => this.normalizeDocStatus(doc) === 'FOR_AMENDMENT',
    );
  }

  get hasMissingRequirements(): boolean {
    return (this.fleet?.missingRequirements || []).length > 0;
  }

  get canApproveEnrollment(): boolean {
    return (
      !this.hasDocumentsForAmendment &&
      this.stagedAmendments.length === 0 &&
      !this.hasMissingRequirements &&
      !this.hasUnviewableRequiredDocuments
    );
  }

  requirementLabel(code: string): string {
    const labels: Record<string, string> = {
      REPRESENTATIVE_ID: 'Valid ID',
      LTFRB_CPC_NUMBER: 'Valid LTFRB CPC Number',
      CPC_EXPIRATION_DATE: 'CPC Expiration Date',
      CPC_DOCUMENT: 'Valid LTFRB CPC Document',
    };
    return labels[code] || code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }

  onDocumentImageError(doc: TaxiFleetDocument): void {
    if (!doc.fleetDocumentId || this.failedDocumentIds.has(doc.fleetDocumentId)) {
      return;
    }
    this.failedDocumentIds = new Set(this.failedDocumentIds);
    this.failedDocumentIds.add(doc.fleetDocumentId);
  }

  isDocumentLoadFailed(doc: TaxiFleetDocument | null | undefined): boolean {
    return !!doc?.fleetDocumentId && this.failedDocumentIds.has(doc.fleetDocumentId);
  }

  documentLoadErrorMessage(): string {
    return 'Document cannot be loaded. Please check and try again.';
  }

  mapEnrollmentActionError(error: any, fallback: string): string {
    const message =
      error?.message ||
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.error?.errors?.[0]?.message ||
      '';
    if (/status.*(changed|change)|no longer|not.*for.?approval|already (approved|declined|amended)/i.test(message)) {
      return 'The status of this enrollment has changed. Please check and try again.';
    }
    return message || fallback;
  }

  get hasUnviewableRequiredDocuments(): boolean {
    return this.reviewableDocuments.some(
      (doc) => !!doc.fleetDocumentId && this.failedDocumentIds.has(doc.fleetDocumentId),
    );
  }

  normalizeDocStatus(doc: TaxiFleetDocument): string {
    return (doc.status || 'FOR_APPROVAL').toUpperCase();
  }

  getDocumentLabelForReview(doc: TaxiFleetDocument): string {
    if (doc.documentType && FLEET_DOC_TYPE_LABELS[doc.documentType]) {
      return FLEET_DOC_TYPE_LABELS[doc.documentType];
    }
    return 'Document';
  }

  getDocumentStatusLabel(doc: TaxiFleetDocument): string {
    const status = this.normalizeDocStatus(doc);
    if (status === 'APPROVED') {
      return 'Approved';
    }
    if (status === 'FOR_AMENDMENT') {
      return 'For Amendment';
    }
    return 'For Review';
  }

  getDocumentStatusClass(doc: TaxiFleetDocument): string {
    const status = this.normalizeDocStatus(doc);
    if (status === 'APPROVED') {
      return 'stat-active';
    }
    if (status === 'FOR_AMENDMENT') {
      return 'stat-amend';
    }
    return 'stat-review';
  }

  canApproveDocument(doc: TaxiFleetDocument): boolean {
    return (
      (this.fleet?.status || '').toUpperCase() === 'FOR_APPROVAL' &&
      this.normalizeDocStatus(doc) === 'FOR_APPROVAL' &&
      !this.isDocumentLoadFailed(doc)
    );
  }

  /** OP2-209: FOR_APPROVAL or APPROVED docs can be tagged; not when already FOR_AMENDMENT */
  canTagDocumentForAmendment(doc: TaxiFleetDocument): boolean {
    if (!this.canReviewDocuments) {
      return false;
    }
    const status = this.normalizeDocStatus(doc);
    return status === 'FOR_APPROVAL' || status === 'APPROVED';
  }

  documentHistory(doc: TaxiFleetDocument): FleetDocumentApprovalHistory[] {
    const history = this.fleet?.documentApprovalHistory || [];
    return history.filter((entry) => entry.fleetDocumentId === doc.fleetDocumentId);
  }

  historyActionLabel(action: string): string {
    return action
      .replace(/^DOCUMENT_/, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  openReviewDocumentModal(
    modal: TemplateRef<any>,
    doc: TaxiFleetDocument,
    status: FleetDocumentReviewStatus,
  ): void {
    this.pendingReviewDoc = doc;
    this.pendingReviewStatus = status;
    this.documentReviewRemarks = status === 'FOR_AMENDMENT' ? doc.remarks || '' : '';
    this.statusFormError = '';
    this.modalService.open(modal, { size: 'md', backdrop: 'static', keyboard: false });
  }

  confirmReviewDocument(modal: NgbModalRef): void {
    if (!this.pendingReviewDoc?.fleetDocumentId || !this.pendingReviewStatus || this.isReviewingDocument) {
      return;
    }

    const remarks = this.documentReviewRemarks.trim();
    if (this.pendingReviewStatus === 'FOR_AMENDMENT' && !remarks) {
      this.statusFormError = 'Message to the user is required.';
      return;
    }
    if (this.pendingReviewStatus === 'FOR_AMENDMENT' && remarks.length > 500) {
      this.statusFormError = 'Remarks must be at most 500 characters';
      return;
    }

    this.isReviewingDocument = true;
    this.statusFormError = '';

    const input: {
      fleetDocumentId: string;
      status: FleetDocumentReviewStatus;
      remarks?: string;
    } = {
      fleetDocumentId: this.pendingReviewDoc.fleetDocumentId,
      status: this.pendingReviewStatus,
    };

    if (this.pendingReviewStatus === 'FOR_AMENDMENT') {
      input.remarks = remarks;
    }

    const taggedForAmendment = this.pendingReviewStatus === 'FOR_AMENDMENT';
    const docTypeLabel =
      (this.pendingReviewDoc?.documentType && FLEET_DOC_TYPE_LABELS[this.pendingReviewDoc.documentType])
      || 'Document';
    const enrollmentStatus = (this.fleet?.status || '').toUpperCase();
    const shouldTagEnrollment =
      taggedForAmendment && (enrollmentStatus === 'FOR_APPROVAL' || enrollmentStatus === 'APPROVED');

    this.apollo.mutate({
      mutation: REVIEW_FLEET_DOCUMENT,
      variables: { input },
    }).subscribe({
      next: (result: any) => {
        this.isReviewingDocument = false;
        const updated = result?.data?.reviewFleetDocument;
        if (updated) {
          this.patchDocument(updated);
          this.toastr.success(
            this.pendingReviewStatus === 'APPROVED'
              ? 'Document approved.'
              : 'Document tagged for amendment.',
          );
          this.fetchFleetDetail(this.fleetId);
        }
        this.pendingReviewDoc = null;
        this.pendingReviewStatus = null;
        this.documentReviewRemarks = '';
        modal.close();

        // A document tagged FOR_AMENDMENT should move the whole enrollment out of
        // the For Approval queue so reviewers see it needs operator action.
        if (shouldTagEnrollment && this.fleet?.fleetId) {
          this.apollo.mutate({
            mutation: TAG_FLEET_ENROLLMENT_FOR_AMENDMENT,
            variables: {
              input: {
                fleetId: this.fleet.fleetId,
                amendments: [{ fieldName: docTypeLabel, message: remarks }],
              },
            },
          }).subscribe({
            next: (tagResult: any) => {
              const updatedFleet = tagResult?.data?.tagFleetEnrollmentForAmendment;
              if (updatedFleet && this.fleet) {
                this.fleet = { ...this.fleet, status: updatedFleet.status };
              }
            },
          });
        }
      },
      error: (error) => {
        this.isReviewingDocument = false;
        this.statusFormError = this.mapEnrollmentActionError(
          error,
          'Submission failed. Please check and try again.',
        );
      },
    });
  }

  private patchDocument(updated: {
    fleetDocumentId: string;
    status?: string | null;
    remarks?: string | null;
    dateUpdated?: string | null;
  }): void {
    if (!this.fleet?.documents) {
      return;
    }

    this.fleet = {
      ...this.fleet,
      documents: this.fleet.documents.map((doc) =>
        doc.fleetDocumentId === updated.fleetDocumentId
          ? {
              ...doc,
              status: updated.status ?? doc.status,
              remarks: updated.remarks ?? doc.remarks,
              dateUpdated: updated.dateUpdated ?? doc.dateUpdated,
            }
          : doc,
      ),
    };
  }

  openApproveModal(modal: TemplateRef<any>): void {
    this.statusFormError = '';
    this.modalService.open(modal, { size: 'md', backdrop: 'static', keyboard: false });
  }

  onApproveEnrollment(modal: NgbModalRef): void {
    if (!this.fleet?.fleetId || this.isUpdatingStatus) {
      return;
    }

    if (!this.canApproveEnrollment) {
      if (this.hasMissingRequirements) {
        this.statusFormError = 'Please complete the required fields.';
      } else if (this.hasUnviewableRequiredDocuments) {
        this.statusFormError = this.documentLoadErrorMessage();
      } else {
        this.statusFormError = 'Unable to approve enrollment. Please try again.';
      }
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';

    this.apollo.mutate({
      mutation: APPROVE_FLEET_ENROLLMENT,
      variables: { input: { fleetId: this.fleet.fleetId } },
    }).subscribe({
      next: (result: any) => {
        this.isUpdatingStatus = false;
        const updated = result?.data?.approveFleetEnrollment;
        if (updated && this.fleet) {
          this.fleet = { ...this.fleet, status: updated.status };
        }
        modal.close();
      },
      error: (error) => {
        this.isUpdatingStatus = false;
        this.statusFormError = this.mapEnrollmentActionError(
          error,
          'Unable to approve enrollment. Please try again.',
        );
        if (this.fleet?.fleetId) {
          this.fetchFleetDetail(this.fleet.fleetId);
        }
      },
    });
  }

  openDeclineEnrollmentModal(modal: TemplateRef<any>): void {
    this.declineRemarks = '';
    this.statusFormError = '';
    this.modalService.open(modal, { size: 'md', backdrop: 'static', keyboard: false });
  }

  confirmDeclineEnrollment(modal: NgbModalRef): void {
    if (!this.fleet?.fleetId || this.isUpdatingStatus) {
      return;
    }

    if (!this.declineRemarks.trim()) {
      this.statusFormError = 'Enter a reason for the declined enrollment';
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';

    this.apollo.mutate({
      mutation: DECLINE_FLEET_ENROLLMENT,
      variables: { input: { fleetId: this.fleet.fleetId, declineRemarks: this.declineRemarks.trim() } },
    }).subscribe({
      next: (result: any) => {
        this.isUpdatingStatus = false;
        const updated = result?.data?.declineFleetEnrollment;
        if (updated && this.fleet) {
          this.fleet = { ...this.fleet, status: updated.status, declineRemarks: updated.declineRemarks };
        }
        modal.close();
      },
      error: (error) => {
        this.isUpdatingStatus = false;
        this.statusFormError = this.mapEnrollmentActionError(error, 'Unable to decline enrollment. Please try again.');
        if (this.fleet?.fleetId) {
          this.fetchFleetDetail(this.fleet.fleetId);
        }
      },
    });
  }

  /** Opens the per-field modal — staging only, no backend call until the summary is confirmed. */
  openTagForAmendmentModal(modal: TemplateRef<any>, fieldName: string): void {
    this.pendingFieldName = fieldName;
    this.pendingMessage = this.getStagedAmendment(fieldName)?.message || '';
    this.statusFormError = '';
    this.modalService.open(modal, { size: 'md', backdrop: 'static', keyboard: false });
  }

  confirmFieldAmendment(modal: NgbModalRef): void {
    if (!this.pendingMessage.trim()) {
      this.statusFormError = 'Message to the user is required.';
      return;
    }

    const existingIndex = this.stagedAmendments.findIndex((item) => item.fieldName === this.pendingFieldName);
    const entry = { fieldName: this.pendingFieldName, message: this.pendingMessage.trim() };

    if (existingIndex >= 0) {
      this.stagedAmendments = this.stagedAmendments.map((item, i) => (i === existingIndex ? entry : item));
    } else {
      this.stagedAmendments = [...this.stagedAmendments, entry];
    }

    modal.close();
  }

  openAmendmentSummaryModal(modal: TemplateRef<any>): void {
    this.statusFormError = '';
    this.modalService.open(modal, { size: 'md', backdrop: 'static', keyboard: false });
  }

  /** Batches every staged field into one tagFleetEnrollmentForAmendment call. */
  confirmSendForAmendment(modal: NgbModalRef): void {
    if (!this.fleet?.fleetId || this.isUpdatingStatus || this.stagedAmendments.length === 0) {
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';

    this.apollo.mutate({
      mutation: TAG_FLEET_ENROLLMENT_FOR_AMENDMENT,
      variables: {
        input: {
          fleetId: this.fleet.fleetId,
          amendments: this.stagedAmendments,
        },
      },
    }).subscribe({
      next: (result: any) => {
        this.isUpdatingStatus = false;
        const updated = result?.data?.tagFleetEnrollmentForAmendment;
        if (updated && this.fleet) {
          this.fleet = { ...this.fleet, status: updated.status };
        }
        this.stagedAmendments = [];
        modal.close();
      },
      error: (error) => {
        this.isUpdatingStatus = false;
        this.statusFormError = this.mapEnrollmentActionError(
          error,
          'Submission failed. Please check and try again.',
        );
        if (this.fleet?.fleetId) {
          this.fetchFleetDetail(this.fleet.fleetId);
        }
      },
    });
  }

  async fetchFleetRegistry(): Promise<void> {
    if (!this.fleetId || this.isRegistryLoading) {
      return;
    }

    this.isRegistryLoading = true;
    this.registryError = '';

    try {
      const items = await this.fetchAllRegistryItems();
      this.registryItems = items.filter(
        (item) => String(item.fleetId) === String(this.fleetId)
      );
    } catch (error: any) {
      this.registryItems = [];
      this.registryError = error?.message || 'Unable to load fleet registry.';
    } finally {
      this.isRegistryLoading = false;
    }
  }

  private async fetchAllRegistryItems(): Promise<FleetRegistryItem[]> {
    const limit = 100;
    let page = 1;
    let totalPages = 1;
    const items: FleetRegistryItem[] = [];

    while (page <= totalPages) {
      const result: any = await firstValueFrom(
        this.apollo.query({
          fetchPolicy: 'network-only',
          query: ADMIN_FLEET_VEHICLE_REGISTRIES,
          variables: {
            input: { page, limit },
          },
        })
      );

      const payload = result?.data?.adminFleetVehicleRegistries;
      const pageItems = (payload?.items || []) as FleetRegistryItem[];
      items.push(...pageItems);
      totalPages = Math.max(1, Number(payload?.totalPages) || 1);
      page += 1;

      if (pageItems.length === 0) {
        break;
      }
    }

    return items;
  }

  getPlateOrConduction(item: FleetRegistryItem): string {
    return item.plateNumber || item.conductionNumber || '—';
  }

  getMakeModelYear(item: FleetRegistryItem): string {
    const make = item.vehicleMaker || item.vehicleType || '';
    const model = item.vehicleModel || '';
    const year = item.yearModel != null ? String(item.yearModel) : '';
    return [make, model, year].filter(Boolean).join(' ') || '—';
  }

  getAssignedDriver(item: FleetRegistryItem): string {
    const partner = item.associatedPartner;
    if (!partner) {
      return '—';
    }

    const name = [partner.firstName, partner.middleName, partner.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    return name || '—';
  }

  getRegistryVehicleStatus(item: FleetRegistryItem): 'vacant' | 'online' | 'offline' {
    if (item.associatedPartner === null || item.associatedPartner === undefined) {
      return 'vacant';
    }
    return item.associatedPartner.isOnline ? 'online' : 'offline';
  }

  getRegistryStatusLabel(item: FleetRegistryItem): string {
    const status = this.getRegistryVehicleStatus(item);
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  getRegistryStatusClass(item: FleetRegistryItem): string {
    const status = this.getRegistryVehicleStatus(item);
    if (status === 'online') {
      return 'stat-active';
    }
    if (status === 'offline') {
      return 'stat-inactive';
    }
    return 'stat-pending';
  }

  // Registry-level lifecycle status (pending/active/inactive/released) — distinct
  // from getRegistryStatusLabel/Class above, which report operational status
  // (vacant/online/offline). The fleet list's vehicle count only counts
  // registries where this is 'active', so surfacing it here explains why a
  // fleet's registry table can list more rows than its summary count.
  getRegistryLifecycleLabel(item: FleetRegistryItem): string {
    const status = String(item.status || '').toLowerCase();
    return status.charAt(0).toUpperCase() + status.slice(1) || '—';
  }

  getRegistryLifecycleClass(item: FleetRegistryItem): string {
    const status = String(item.status || '').toLowerCase();
    if (status === 'active') {
      return 'stat-active';
    }
    if (status === 'inactive' || status === 'released') {
      return 'stat-inactive';
    }
    return 'stat-pending';
  }

  /* Activate / Deactivate temporarily disabled
  // Activate for any non-active status (pending / inactive / released), matching fleet registry list.
  canActivateRegistry(item: FleetRegistryItem): boolean {
    return (item.status || '').toLowerCase() !== 'active';
  }

  canDeactivateRegistry(item: FleetRegistryItem): boolean {
    return (item.status || '').toLowerCase() === 'active';
  }
  */

  onViewRegistryDetails(item: FleetRegistryItem, drawer: TemplateRef<any>): void {
    this.selectedRegistry = item;
    this.offcanvasService.open(drawer, { position: 'end', panelClass: 'registry-details-offcanvas' });
  }

  /* Activate / Deactivate temporarily disabled
  openRegistryDeactivateModal(modal: TemplateRef<any>, item: FleetRegistryItem): void {
    this.selectedRegistry = item;
    this.modalService.open(modal, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  openRegistryActivateModal(modal: TemplateRef<any>, item: FleetRegistryItem): void {
    this.selectedRegistry = item;
    this.modalService.open(modal, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  confirmRegistryDeactivate(modal: NgbModalRef): void {
    this.updateRegistryStatus('inactive', () => modal.close());
  }

  confirmRegistryActivate(modal: NgbModalRef): void {
    this.updateRegistryStatus('active', () => modal.close());
  }

  private updateRegistryStatus(status: RegistryStatus, onSuccess?: () => void): void {
    if (!this.selectedRegistry?.fleetVehicleRegistryId || this.isUpdatingRegistryStatus) {
      return;
    }

    this.isUpdatingRegistryStatus = true;

    this.apollo.mutate({
      mutation: ADMIN_UPDATE_FLEET_VEHICLE_REGISTRY_STATUS,
      variables: {
        input: {
          fleetVehicleRegistryId: this.selectedRegistry.fleetVehicleRegistryId,
          status,
        },
      },
    }).subscribe({
      next: () => {
        this.isUpdatingRegistryStatus = false;
        onSuccess?.();
        this.selectedRegistry = null;
        void this.fetchFleetRegistry();
      },
      error: () => {
        this.isUpdatingRegistryStatus = false;
        alert('Unable to update vehicle registry status. Please try again.');
      },
    });
  }
  */

  getStatusLabel(status: string): string {
    const normalizedStatus = (status || '').toLowerCase();
    if (normalizedStatus === 'for_approval' || normalizedStatus === 'enrollment') {
      return 'For Approval';
    }

    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  getStatusClass(status: string): string {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === 'active' || normalizedStatus === 'approved') {
      return 'stat-active';
    }

    if (normalizedStatus === 'for_approval') {
      return 'stat-approval';
    }

    if (normalizedStatus === 'for_amendment') {
      return 'stat-amend';
    }

    if (normalizedStatus === 'decline' || normalizedStatus === 'declined' || normalizedStatus === 'rejected') {
      return 'stat-declined';
    }

    if (normalizedStatus === 'inactive' || normalizedStatus === 'deactivated') {
      return 'stat-inactive';
    }

    return 'stat-pending';
  }

  isActiveStatus(status: string): boolean {
    const normalizedStatus = status.toLowerCase();
    return normalizedStatus === 'active' || normalizedStatus === 'approved';
  }

  /** OP2-119: Activate for Inactive when enrollment requirements are complete. */
  canActivateOperator(status: string): boolean {
    const normalizedStatus = (status || '').toLowerCase();
    const isInactive =
      normalizedStatus === 'inactive' || normalizedStatus === 'deactivated';
    return isInactive && !this.hasMissingRequirements;
  }

  /** OP2-119: Deactivate for Active/Approved. */
  canDeactivateOperator(status: string): boolean {
    return this.isActiveStatus(status);
  }

  get isDeclineSelected(): boolean {
    return this.selectedStatus === 'DECLINE';
  }

  openDeactivateModal(modal: TemplateRef<any>): void {
    this.declineRemarks = '';
    this.statusFormError = '';
    this.statusUpdateError = '';
    this.modalService.open(modal, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  confirmDeactivate(modal: NgbModalRef): void {
    // Notes are UI-only today; deactivateFleetOperatorInput only accepts fleetId.
    this.deactivateFleetOperator(() => modal.close());
  }

  private deactivateFleetOperator(onSuccess?: () => void): void {
    const currentFleet = this.fleet;
    if (!currentFleet?.fleetId || this.isUpdatingStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';
    this.statusUpdateError = '';

    this.apollo
      .mutate({
        mutation: DEACTIVATE_FLEET_OPERATOR,
        variables: {
          input: {
            fleetId: currentFleet.fleetId,
          },
        },
      })
      .subscribe({
        next: (result: any) => {
          const updatedFleet = result?.data?.deactivateFleetOperator;

          if (updatedFleet) {
            this.fleet = {
              ...currentFleet,
              status: updatedFleet.status ?? currentFleet.status,
              declineRemarks: updatedFleet.declineRemarks ?? currentFleet.declineRemarks,
            };
            this.isUpdatingStatus = false;
            onSuccess?.();
            return;
          }

          this.isUpdatingStatus = false;
          this.statusFormError =
            result?.errors?.[0]?.message ||
            'Unable to deactivate fleet operator. Please try again.';
        },
        error: (error) => {
          this.isUpdatingStatus = false;
          this.statusFormError = this.mapEnrollmentActionError(
            error,
            'Unable to deactivate fleet operator. Please try again.',
          );
        },
      });
  }

  openActivateModal(modal: TemplateRef<any>): void {
    this.statusFormError = '';
    this.statusUpdateError = '';
    this.modalService.open(modal, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  confirmActivate(modal: NgbModalRef): void {
    if (this.hasMissingRequirements) {
      this.statusFormError = 'Please complete the required fields.';
      return;
    }

    this.activateFleetOperator(() => modal.close());
  }

  private activateFleetOperator(onSuccess?: () => void): void {
    const currentFleet = this.fleet;
    if (!currentFleet?.fleetId || this.isUpdatingStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.statusFormError = '';
    this.statusUpdateError = '';

    this.apollo
      .mutate({
        mutation: ACTIVATE_FLEET_OPERATOR,
        variables: {
          input: {
            fleetId: currentFleet.fleetId,
          },
        },
      })
      .subscribe({
        next: (result: any) => {
          const updatedFleet = result?.data?.activateFleetOperator;

          if (updatedFleet) {
            this.fleet = {
              ...currentFleet,
              status: updatedFleet.status ?? currentFleet.status,
              declineRemarks: updatedFleet.declineRemarks ?? currentFleet.declineRemarks,
            };
            this.isUpdatingStatus = false;
            onSuccess?.();
            return;
          }

          this.isUpdatingStatus = false;
          this.statusFormError =
            result?.errors?.[0]?.message ||
            'Unable to activate fleet operator. Please try again.';
        },
        error: (error) => {
          this.isUpdatingStatus = false;
          this.statusFormError = this.mapEnrollmentActionError(
            error,
            'Unable to activate fleet operator. Please try again.',
          );
        },
      });
  }

  openUpdateStatusModal(modal?: TemplateRef<any>): void {
    const targetModal = modal || this.updateStatusModal;
    if (!targetModal) {
      return;
    }

    this.selectedStatus = 'APPROVED';
    this.declineRemarks = this.fleet?.declineRemarks || '';
    this.statusFormError = '';
    this.statusUpdateError = '';

    this.modalService.open(targetModal, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  onStatusChange(): void {
    this.statusFormError = '';
    if (!this.isDeclineSelected) {
      this.declineRemarks = '';
    }
  }

  confirmUpdateStatus(modal: NgbModalRef): void {
    if (!this.selectedStatus) {
      this.statusFormError = 'Please select a status.';
      return;
    }

    if (this.isDeclineSelected && !this.declineRemarks.trim()) {
      this.statusFormError = 'Please provide decline remarks.';
      return;
    }

    this.updateFleetStatus(
      this.selectedStatus,
      this.isDeclineSelected ? this.declineRemarks.trim() : undefined,
      () => modal.close()
    );
  }

  private updateFleetStatus(
    status: TaxiFleetStatus,
    declineRemarks?: string,
    onSuccess?: () => void
  ): void {
    if (!this.fleet?.fleetId || this.isUpdatingStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.statusUpdateError = '';
    this.statusFormError = '';

    const input: {
      fleetId: string;
      status: TaxiFleetStatus;
      declineRemarks?: string;
    } = {
      fleetId: this.fleet.fleetId,
      status,
    };

    if (declineRemarks) {
      input.declineRemarks = declineRemarks;
    }

    this.apollo.mutate({
      mutation: UPDATE_TAXI_FLEET_ACCOUNT_STATUS,
      variables: { input },
    }).subscribe({
      next: (result: any) => {
        const updatedFleet = result?.data?.updateFleetStatus;

        if (updatedFleet) {
          this.fleet = {
            ...this.fleet!,
            ...updatedFleet,
            accountHolder: this.fleet!.accountHolder,
          };
          this.isUpdatingStatus = false;
          onSuccess?.();
          return;
        }

        this.isUpdatingStatus = false;
        this.statusFormError = 'Unable to update fleet status. Please try again.';
      },
      error: (error) => {
        this.isUpdatingStatus = false;
        this.statusFormError = error?.message || 'Unable to update fleet status. Please try again.';
      },
    });
  }

  get isEditCpcDisabled(): boolean {
    return this.editForm.fleetType === 'tnvs';
  }

  onEdit(modal: TemplateRef<any>): void {
    if (!this.fleet) {
      return;
    }

    const account = this.fleet.accountHolder;
    this.editForm = {
      operatorName: this.fleet.operatorName || '',
      registeredBusinessName: this.fleet.registeredBusinessName || '',
      representativeFirstName: this.fleet.representativeFirstName || '',
      representativeLastName: this.fleet.representativeLastName || '',
      representativeMobile: this.fleet.representativeMobile || '',
      representativeEmail: this.fleet.representativeEmail || '',
      ltfrbCpcNumber: this.fleet.ltfrbCpcNumber || '',
      fleetType: (this.fleet.fleetType || 'taxi').toLowerCase(),
      cpcExpirationDate: this.fleet.cpcExpirationDate || '',
      allowedActiveVehicles:
        this.fleet.allowedActiveVehicles != null ? String(this.fleet.allowedActiveVehicles) : '',
      adminFirstName: account?.firstName || '',
      adminLastName: account?.lastName || '',
      adminEmail: account?.email || '',
      adminMobile: account?.mobile || '',
    };
    this.editFieldErrors = {};
    this.editFormError = '';
    this.modalService.open(modal, { size: 'lg', backdrop: 'static', keyboard: false });
  }

  onEditFleetTypeChange(): void {
    if (this.isEditCpcDisabled) {
      this.editForm.ltfrbCpcNumber = '';
      this.editForm.cpcExpirationDate = '';
    }
  }

  onAllowedActiveVehiclesInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 4);
    if (digits !== input.value) {
      input.value = digits;
    }
    this.editForm.allowedActiveVehicles = digits;
  }

  hasEditFieldError(field: keyof FleetEditForm): boolean {
    return !!this.editFieldErrors[field];
  }

  confirmEditFleet(modal: NgbModalRef): void {
    if (!this.fleet?.fleetId || this.isSavingEdit) {
      return;
    }

    this.editFieldErrors = {};
    this.editFormError = '';

    let hasErrors = false;
    let hasMobileErrors = false;

    const requiredFields: Array<{ key: keyof FleetEditForm; message: string }> = [
      { key: 'operatorName', message: 'Enter Taxi Fleet Operator Name' },
      { key: 'registeredBusinessName', message: 'Enter Registered Business Name' },
      { key: 'representativeFirstName', message: 'Enter First Name' },
      { key: 'representativeLastName', message: 'Enter Last Name' },
      { key: 'representativeMobile', message: 'Enter Mobile Number' },
      { key: 'allowedActiveVehicles', message: 'Enter number of allowed active vehicles' },
      { key: 'adminFirstName', message: 'Enter Admin First Name' },
      { key: 'adminLastName', message: 'Enter Admin Last Name' },
      { key: 'adminEmail', message: 'Enter Email Address' },
      { key: 'adminMobile', message: 'Enter Mobile Number' },
    ];

    for (const field of requiredFields) {
      if (!this.editForm[field.key].trim()) {
        this.editFieldErrors[field.key] = field.message;
        hasErrors = true;
      }
    }

    if (this.editForm.representativeEmail.trim() && !EMAIL_REGEX.test(this.editForm.representativeEmail.trim())) {
      this.editFieldErrors.representativeEmail = 'Please enter a valid email address';
      hasErrors = true;
    }

    if (!PH_MOBILE_REGEX.test(this.editForm.representativeMobile.trim())) {
      this.editFieldErrors.representativeMobile = 'Please enter a PH mobile number';
      hasMobileErrors = true;
    }

    if (!EMAIL_REGEX.test(this.editForm.adminEmail.trim())) {
      this.editFieldErrors.adminEmail = 'Please enter a valid email address';
      hasErrors = true;
    }

    if (!PH_MOBILE_REGEX.test(this.editForm.adminMobile.trim())) {
      this.editFieldErrors.adminMobile = 'Please enter a PH mobile number';
      hasMobileErrors = true;
    }

    if (!this.isEditCpcDisabled) {
      if (!this.editForm.ltfrbCpcNumber.trim()) {
        this.editFieldErrors.ltfrbCpcNumber = 'Enter Valid LTFRB CPC Number';
        hasErrors = true;
      }
      if (!this.editForm.cpcExpirationDate.trim()) {
        this.editFieldErrors.cpcExpirationDate = 'Enter CPC Expiration Date';
        hasErrors = true;
      }
    }

    if (hasErrors || hasMobileErrors) {
      this.editFormError = hasMobileErrors
        ? 'Please complete the required fields and enter valid mobile numbers (09XXXXXXXXX / 639XXXXXXXXX).'
        : 'Please complete the required fields.';
      return;
    }

    this.isSavingEdit = true;

    this.apollo.mutate({
      mutation: UPDATE_FLEET_OPERATOR_DETAILS,
      variables: {
        input: {
          fleetId: this.fleet.fleetId,
          operatorName: this.editForm.operatorName.trim(),
          registeredBusinessName: this.editForm.registeredBusinessName.trim(),
          representativeFirstName: this.editForm.representativeFirstName.trim(),
          representativeLastName: this.editForm.representativeLastName.trim(),
          representativeMobile: this.editForm.representativeMobile.trim(),
          representativeEmail: this.editForm.representativeEmail.trim() || null,
          ltfrbCpcNumber: this.isEditCpcDisabled ? null : this.editForm.ltfrbCpcNumber.trim() || null,
          fleetType: this.editForm.fleetType,
          cpcExpirationDate: this.isEditCpcDisabled ? null : this.editForm.cpcExpirationDate.trim() || null,
          allowedActiveVehicles: parseInt(this.editForm.allowedActiveVehicles, 10),
          adminFirstName: this.editForm.adminFirstName.trim(),
          adminLastName: this.editForm.adminLastName.trim(),
          adminEmail: this.editForm.adminEmail.trim(),
          adminMobile: this.editForm.adminMobile.trim(),
        },
      },
    }).subscribe({
      next: (result: any) => {
        this.isSavingEdit = false;
        const updated = result?.data?.updateFleetOperatorDetails;
        if (!updated || !this.fleet) {
          this.editFormError = 'Unable to update fleet details. Please try again.';
          return;
        }

        this.fleet = {
          ...this.fleet,
          operatorName: updated.operatorName,
          registeredBusinessName: updated.registeredBusinessName,
          representativeFirstName: updated.representativeFirstName,
          representativeLastName: updated.representativeLastName,
          representativeMobile: updated.representativeMobile,
          representativeEmail: updated.representativeEmail,
          ltfrbCpcNumber: updated.ltfrbCpcNumber,
          fleetType: updated.fleetType,
          cpcExpirationDate: updated.cpcExpirationDate,
          allowedActiveVehicles: updated.allowedActiveVehicles,
          dateUpdated: updated.dateUpdated,
          accountHolder: updated.accountHolder
            ? {
                ...this.fleet.accountHolder!,
                firstName: updated.accountHolder.firstName,
                lastName: updated.accountHolder.lastName,
                email: updated.accountHolder.email,
                mobile: updated.accountHolder.mobile,
              }
            : this.fleet.accountHolder,
        };
        this.toastr.success('Fleet Operator details updated.');
        modal.close();
      },
      error: (error) => {
        this.isSavingEdit = false;
        this.editFormError = error?.message || 'Unable to update fleet details. Please try again.';
      },
    });
  }

  onResendCredentials(modal: TemplateRef<any>): void {
    if (!this.fleet?.fleetId || this.isResendingCredentials) {
      return;
    }
    this.editFormError = '';
    this.modalService.open(modal, { size: 'md', backdrop: 'static', keyboard: false });
  }

  confirmResendCredentials(modal: NgbModalRef): void {
    if (!this.fleet?.fleetId || this.isResendingCredentials) {
      return;
    }

    this.isResendingCredentials = true;

    this.apollo.mutate({
      mutation: RESEND_FLEET_LOGIN_CREDENTIALS,
      variables: { input: { fleetId: this.fleet.fleetId } },
    }).subscribe({
      next: (result: any) => {
        this.isResendingCredentials = false;
        if (result?.data?.resendFleetLoginCredentials) {
          this.toastr.success('Login credentials resent to the Fleet Operator admin.');
          modal.close();
          return;
        }
        this.toastr.error('Unable to resend login credentials. Please try again.');
      },
      error: (error) => {
        this.isResendingCredentials = false;
        this.toastr.error(error?.message || 'Unable to resend login credentials. Please try again.');
      },
    });
  }

  onResendAmendmentEmail(): void {
    // BE has no resend-amendment-email mutation yet (schema: only resendFleetLoginCredentials).
    this.toastr.info('Resend For Amendment Email is not available yet. Please try again later.');
  }

  onResendEmailVerification(): void {
    // TODO: wire resend email verification when available
  }

  onResendMobileVerification(): void {
    // TODO: wire resend mobile verification when available
  }
}
